import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import usePermissions from "hooks/usePermissions";
import { FilterConfig } from "types/Common";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";

export default function useQuoteFilters(isV2 = false) {
  const [searchParams] = useSearchParams();
  const status: string = searchParams.get("display_status") || "";
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { permissions }: any = usePermissions();
  const { provider_teams, accessible_companies } = JSON.parse(permissions);

  const QUOTES_DISPLAY_STATUS = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: "quote.listing.openForQuotations", defaultMessage: "Open for quotations" }),
        value: "open_for_quotations",
      },
      {
        label: intl.formatMessage({ id: "quote.listing.closedForQuotation", defaultMessage: "Closed for quotations" }),
        value: "closed_for_quotations",
      },
      {
        label: intl.formatMessage({ id: "quote.listing.awaitingSelection", defaultMessage: "Awaiting selection" }),
        value: "awaiting_action",
      },
      {
        label: intl.formatMessage({ id: "general.canceled", defaultMessage: "Canceled" }),
        value: "canceled",
      },
    ],
    [intl],
  );

  const config: FilterConfig[] = [
    {
      filterType: "multiselect",
      label: intl.formatMessage({ id: "general.displayStatus", defaultMessage: "Display status" }),
      key: "display_status",
      options: QUOTES_DISPLAY_STATUS,
    },
    {
      filterType: "multiselect",
      label: intl.formatMessage({ id: "general.team", defaultMessage: "Team" }),
      key: "assigned_team",
      isSearchable: true,
      options: provider_teams?.teams ?? [],
    },
    {
      filterType: "freight_mode",
      label: intl.formatMessage({ id: "general.freightMode", defaultMessage: "Freight mode" }),
      key: "freight_mode",
      filterValueKey: isV2 && !["draft", "archived"].includes(status) ? "value" : "key",
    },
    {
      filterType: "accordion",
      label: intl.formatMessage({ id: "general.origin", defaultMessage: "Origin" }),
      key: "origin",
      nestedFilters: [
        {
          filterType: "date",
          label: intl.formatMessage({ id: "general.pickupDate", defaultMessage: "Pickup date" }),
          key: "pickup_date",
        },
        {
          filterType: "text",
          label: intl.formatMessage({ id: "general.pickupCity", defaultMessage: "Pickup city" }),
          key: "pickup_city",
          ...(isV2 &&
            !["draft", "archived"].includes(status) && {
              options: [
                ...new Set(
                  queryClient
                    .getQueryData<Array<any>>(quoteRequestQueryKeys.allQuotes)
                    ?.map((item) => item?.pickup_city)
                    ?.filter((item) => item),
                ),
              ],
            }),
        },
      ],
    },
    {
      filterType: "accordion",
      label: intl.formatMessage({ id: "general.destination", defaultMessage: "Destination" }),
      key: "destination",
      nestedFilters: [
        {
          filterType: "date",
          label: intl.formatMessage({ id: "report.DeliveryDate", defaultMessage: "Delivery date" }),
          key: "delivery_date",
        },
        {
          filterType: "text",
          label: intl.formatMessage({ id: "general.deliveryCity", defaultMessage: "Delivery city" }),
          key: "delivery_city",
          ...(isV2 &&
            !["draft", "archived"].includes(status) && {
              options: [
                ...new Set(
                  queryClient
                    .getQueryData<Array<any>>(quoteRequestQueryKeys.allQuotes)
                    ?.map((item) => item?.stop1_city)
                    ?.filter((item) => item),
                ),
              ],
            }),
        },
      ],
    },
    {
      filterType: "multiselect",
      label: intl.formatMessage({ id: "general.shipper", defaultMessage: "Shipper" }),
      key: "shipper",
      isSearchable: true,
      options: accessible_companies.map((company: any) => ({
        value: company.id,
        label: company.name,
      })),
    },
  ];

  return { config };
}
